import { useState, useEffect } from "react";
import { 
  BarChart3, Clock, Users, MapPin, Wrench, RefreshCw, TrendingUp, HelpCircle
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


function StatistiquesParcoursPage({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('global'); // global, amo, dpt, equipe

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(API + "/api/analyse/statistiques-parcours");
      if (!response.ok) throw new Error("Erreur chargement");
      const result = await response.json();
      setData(result);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  // Labels des métriques
  const etapesLabels = {
    "vt_amo_to_depot": "VT AMO → Dépôt",
    "depot_to_octroi": "Dépôt → Octroi",
    "delai_avance": "Délai Avance 30%",
    "delai_solde": "Délai Solde 70%",
    "duree_chantier": "Durée Chantier",
  };

  // Descriptions pour les tooltips
  const etapesDescriptions = {
    "vt_amo_to_depot": "Délai entre la visite technique AMO et le dépôt du dossier à l'ANAH",
    "depot_to_octroi": "Délai entre le dépôt du dossier ANAH et l'obtention de l'octroi (accord de financement)",
    "delai_avance": "Délai entre la demande d'avance et son encaissement (pour les dossiers avec 30% avance ANAH)",
    "delai_solde": "Délai entre la demande de solde et sa réception (pour les dossiers avec 70% solde ANAH)",
    "duree_chantier": "Durée totale du chantier (du début jusqu'à la réception ou levée des réserves)",
  };

  // Couleurs des cartes
  const etapesColors = {
    "vt_amo_to_depot": "from-violet-600 to-violet-700",
    "depot_to_octroi": "from-purple-600 to-purple-700",
    "delai_avance": "from-emerald-600 to-emerald-700",
    "delai_solde": "from-teal-600 to-teal-700",
    "duree_chantier": "from-amber-600 to-amber-700",
  };

  // Labels pour AMO (2 métriques)
  const amoMetrics = ["vt_amo_to_depot", "depot_to_octroi"];
  
  // Labels pour Départements (3 métriques)
  const dptMetrics = ["depot_to_octroi", "delai_avance", "delai_solde"];

  // Fonction pour déterminer la couleur de performance
  const getPerformanceColor = (value, moyenneGlobale) => {
    if (!value || !moyenneGlobale) return "text-white";
    const ratio = value / moyenneGlobale;
    if (ratio <= 0.7) return "text-green-400 font-bold";
    if (ratio <= 0.9) return "text-green-300";
    if (ratio <= 1.1) return "text-yellow-300";
    if (ratio <= 1.3) return "text-orange-400";
    return "text-red-400 font-bold";
  };

  const getPerformanceBg = (value, moyenneGlobale) => {
    if (!value || !moyenneGlobale) return "";
    const ratio = value / moyenneGlobale;
    if (ratio <= 0.7) return "bg-green-500/20";
    if (ratio <= 0.9) return "bg-green-500/10";
    if (ratio <= 1.1) return "";
    if (ratio <= 1.3) return "bg-orange-500/10";
    return "bg-red-500/20";
  };

  // Composant pour en-tête avec tooltip
  const HeaderWithTooltip = ({ etapeKey, short = false }) => (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <div className="flex items-center justify-center gap-1 cursor-help">
          <span className={short ? "text-xs" : ""}>{short ? etapesLabels[etapeKey]?.split(" → ")[1] || etapesLabels[etapeKey] : etapesLabels[etapeKey]}</span>
          <HelpCircle className="w-3 h-3 text-slate-500" />
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs bg-slate-800 text-white border border-slate-600 p-3">
        <p className="font-semibold text-blue-300 mb-1">{etapesLabels[etapeKey]}</p>
        <p className="text-sm text-slate-300">{etapesDescriptions[etapeKey]}</p>
      </TooltipContent>
    </Tooltip>
  );

  // Cellule avec indicateur de performance
  const PerformanceCell = ({ value, moyenneGlobale }) => {
    if (!value) return <span className="text-slate-500">-</span>;
    
    const colorClass = getPerformanceColor(value, moyenneGlobale);
    const bgClass = getPerformanceBg(value, moyenneGlobale);
    const ratio = moyenneGlobale ? (value / moyenneGlobale) : 1;
    const diff = moyenneGlobale ? Math.round((ratio - 1) * 100) : 0;
    const diffText = diff > 0 ? `+${diff}%` : `${diff}%`;
    
    if (!moyenneGlobale) {
      return <span className={colorClass}>{value}j</span>;
    }
    
    return (
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <span className={`${colorClass} ${bgClass} px-2 py-1 rounded cursor-help`}>
            {value}j
          </span>
        </TooltipTrigger>
        <TooltipContent className="bg-slate-800 text-white border border-slate-600 p-2">
          <div className="text-xs">
            <p>Moyenne globale: <span className="text-blue-300">{moyenneGlobale}j</span></p>
            <p className={colorClass}>Écart: {diffText}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-8">
        <div className="flex items-center justify-center gap-3">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
          <span className="text-slate-400">Chargement des statistiques...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-red-500/50 p-8 text-center">
        <p className="text-red-400">{error}</p>
        <button onClick={loadData} className="mt-4 px-4 py-2 bg-red-600 rounded-lg text-white">Réessayer</button>
      </div>
    );
  }

  // Préparer données pour graphique global
  const chartDataGlobal = data?.moyennesGlobales ? 
    Object.entries(data.moyennesGlobales).map(([key, val]) => ({
      name: etapesLabels[key] || key,
      moyenne: val.moyenne,
      count: val.count,
    })) : [];

  // Données équipes pour graphique
  const chartDataEquipe = data?.dureeChantierParEquipe ?
    Object.entries(data.dureeChantierParEquipe)
      .filter(([eq, _]) => eq && eq !== "Non assignée")
      .sort((a, b) => b[1].moyenne - a[1].moyenne)
      .slice(0, 10)
      .map(([equipe, stats]) => ({
        name: equipe.length > 20 ? equipe.substring(0, 20) + "..." : equipe,
        fullName: equipe,
        moyenne: stats.moyenne,
        count: stats.count,
      })) : [];

  return (
    <TooltipProvider>
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl border border-blue-700/30 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-blue-100">📊 Statistiques du Parcours Client</h2>
              <p className="text-blue-200/60">Durées moyennes entre étapes clés - {data?.totalDossiers || 0} dossiers analysés</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onBack && (
              <button onClick={onBack} className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg text-white transition-colors">
                ← Retour
              </button>
            )}
            <button onClick={loadData} className="p-3 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-colors">
              <RefreshCw className="w-5 h-5 text-slate-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-2 flex-wrap">
        <button 
          onClick={() => setViewMode('global')}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${viewMode === 'global' ? 'bg-blue-600 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'}`}
        >
          <TrendingUp className="w-4 h-4" />
          Vue globale
        </button>
        <button 
          onClick={() => setViewMode('amo')}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${viewMode === 'amo' ? 'bg-purple-600 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'}`}
        >
          <Users className="w-4 h-4" />
          Par AMO
        </button>
        <button 
          onClick={() => setViewMode('dpt')}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${viewMode === 'dpt' ? 'bg-green-600 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'}`}
        >
          <MapPin className="w-4 h-4" />
          Par Département
        </button>
        <button 
          onClick={() => setViewMode('equipe')}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${viewMode === 'equipe' ? 'bg-amber-600 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'}`}
        >
          <Wrench className="w-4 h-4" />
          Par Équipe de pose
        </button>
      </div>

      {/* === VUE GLOBALE === */}
      {viewMode === 'global' && (
        <div className="space-y-6">
          {/* Cartes des moyennes */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-400" />
              Durées moyennes entre étapes (en jours)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {data?.moyennesGlobales && Object.entries(data.moyennesGlobales).map(([key, val]) => (
                <Tooltip key={key} delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div className={`bg-gradient-to-br ${etapesColors[key] || 'from-slate-600 to-slate-700'} rounded-xl p-4 border border-white/10 cursor-help`}>
                      <div className="text-white/80 text-xs font-medium mb-2">{etapesLabels[key]}</div>
                      <div className="text-white font-bold text-2xl">{val.moyenne}j</div>
                      <div className="text-white/60 text-xs mt-1">
                        Min: {val.min}j | Max: {val.max}j
                      </div>
                      <div className="text-white/50 text-xs">
                        ({val.count} dossiers)
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-slate-800 text-white border border-slate-600 p-3">
                    <p className="text-sm text-slate-300">{etapesDescriptions[key]}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>

          {/* Graphique */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
            <h3 className="text-white font-semibold mb-4">Comparaison des durées moyennes</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartDataGlobal} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9CA3AF" unit="j" />
                  <YAxis type="category" dataKey="name" stroke="#9CA3AF" width={140} fontSize={11} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#F9FAFB' }}
                    formatter={(value) => [`${value} jours`, 'Moyenne']}
                  />
                  <Bar dataKey="moyenne" fill="#3B82F6" name="Moyenne (jours)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* === VUE PAR AMO === */}
      {viewMode === 'amo' && (
        <div className="space-y-6">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              Performances AMO : VT AMO → Dépôt et Dépôt → Octroi
            </h3>
            
            {/* Légende */}
            <div className="flex flex-wrap gap-4 mb-4 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 bg-green-500/30 rounded"></span>
                <span className="text-green-400">Rapide (≤-10%)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 bg-yellow-500/20 rounded"></span>
                <span className="text-yellow-300">Dans la moyenne (±10%)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 bg-red-500/30 rounded"></span>
                <span className="text-red-400">Lent (≥+30%)</span>
              </div>
            </div>
            
            {/* Tableau AMO */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-slate-400">AMO</th>
                    {amoMetrics.map(key => (
                      <th key={key} className="text-center px-4 py-3 text-slate-400">
                        <HeaderWithTooltip etapeKey={key} />
                      </th>
                    ))}
                    <th className="text-center px-4 py-3 text-slate-400">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {data?.moyennesParAmo && Object.entries(data.moyennesParAmo)
                    .filter(([amo, _]) => amo && amo !== "Non assigné")
                    .map(([amo, stats]) => {
                      // Calculer score
                      let totalRatio = 0;
                      let count = 0;
                      amoMetrics.forEach(key => {
                        if (stats[key] && data.moyennesGlobales[key]) {
                          totalRatio += stats[key].moyenne / data.moyennesGlobales[key].moyenne;
                          count++;
                        }
                      });
                      const score = count > 0 ? Math.round((2 - totalRatio / count) * 50) : 50;
                      return { amo, stats, score };
                    })
                    .sort((a, b) => b.score - a.score)
                    .map(({ amo, stats, score }) => (
                      <tr key={amo} className="hover:bg-slate-700/30">
                        <td className="px-4 py-3 text-white font-medium">{amo}</td>
                        {amoMetrics.map(key => (
                          <td key={key} className="text-center px-4 py-3">
                            <PerformanceCell 
                              value={stats[key]?.moyenne} 
                              moyenneGlobale={data.moyennesGlobales[key]?.moyenne}
                            />
                          </td>
                        ))}
                        <td className="text-center px-4 py-3">
                          <span className={`px-2 py-1 rounded font-bold ${
                            score >= 60 ? 'bg-green-500/20 text-green-400' :
                            score >= 40 ? 'bg-yellow-500/20 text-yellow-300' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {score}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* === VUE PAR DÉPARTEMENT === */}
      {viewMode === 'dpt' && (
        <div className="space-y-6">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-400" />
              Performances Départements : Octroi & Délais Avances/Soldes
            </h3>
            
            {/* Légende */}
            <div className="flex flex-wrap gap-4 mb-4 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 bg-green-500/30 rounded"></span>
                <span className="text-green-400">Rapide (≤-10%)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 bg-yellow-500/20 rounded"></span>
                <span className="text-yellow-300">Dans la moyenne (±10%)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 bg-red-500/30 rounded"></span>
                <span className="text-red-400">Lent (≥+30%)</span>
              </div>
            </div>

            <p className="text-slate-400 text-sm mb-4">
              💡 <strong>Délai Avance 30%</strong> : Dossiers avec "30% avance ANAH" | <strong>Délai Solde 70%</strong> : Dossiers avec "70% ANAH"
            </p>
            
            {/* Tableau Département */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-slate-400">Département</th>
                    {dptMetrics.map(key => (
                      <th key={key} className="text-center px-4 py-3 text-slate-400">
                        <HeaderWithTooltip etapeKey={key} />
                      </th>
                    ))}
                    <th className="text-center px-4 py-3 text-slate-400">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {data?.moyennesParDepartement && Object.entries(data.moyennesParDepartement)
                    .filter(([dpt, _]) => dpt && dpt !== "Non renseigné")
                    .map(([dpt, stats]) => {
                      // Calculer score
                      let totalRatio = 0;
                      let count = 0;
                      dptMetrics.forEach(key => {
                        if (stats[key] && data.moyennesGlobales[key]) {
                          totalRatio += stats[key].moyenne / data.moyennesGlobales[key].moyenne;
                          count++;
                        }
                      });
                      const score = count > 0 ? Math.round((2 - totalRatio / count) * 50) : 50;
                      return { dpt, stats, score };
                    })
                    .sort((a, b) => b.score - a.score)
                    .map(({ dpt, stats, score }) => (
                      <tr key={dpt} className="hover:bg-slate-700/30">
                        <td className="px-4 py-3 text-white font-medium">{dpt}</td>
                        {dptMetrics.map(key => (
                          <td key={key} className="text-center px-4 py-3">
                            <PerformanceCell 
                              value={stats[key]?.moyenne} 
                              moyenneGlobale={data.moyennesGlobales[key]?.moyenne}
                            />
                          </td>
                        ))}
                        <td className="text-center px-4 py-3">
                          <span className={`px-2 py-1 rounded font-bold ${
                            score >= 60 ? 'bg-green-500/20 text-green-400' :
                            score >= 40 ? 'bg-yellow-500/20 text-yellow-300' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {score}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* === VUE PAR ÉQUIPE === */}
      {viewMode === 'equipe' && (
        <div className="space-y-6">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-400" />
              Durée moyenne des chantiers par Équipe de pose
            </h3>
            
            {/* Graphique équipes */}
            <div className="h-80 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartDataEquipe} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9CA3AF" unit="j" />
                  <YAxis type="category" dataKey="name" stroke="#9CA3AF" width={150} fontSize={11} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#F9FAFB' }}
                    formatter={(value) => [`${value} jours`, 'Durée moyenne']}
                  />
                  <Bar dataKey="moyenne" fill="#F59E0B" name="Durée moyenne" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Tableau détaillé */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-slate-400">Équipe de pose</th>
                    <th className="text-center px-4 py-3 text-slate-400">Chantiers terminés</th>
                    <th className="text-center px-4 py-3 text-slate-400">Durée moyenne</th>
                    <th className="text-center px-4 py-3 text-slate-400">Min</th>
                    <th className="text-center px-4 py-3 text-slate-400">Max</th>
                    <th className="text-center px-4 py-3 text-slate-400">Écart-type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {data?.dureeChantierParEquipe && Object.entries(data.dureeChantierParEquipe)
                    .filter(([eq, _]) => eq && eq !== "Non assignée")
                    .sort((a, b) => b[1].count - a[1].count)
                    .map(([equipe, stats]) => (
                      <tr key={equipe} className="hover:bg-slate-700/30">
                        <td className="px-4 py-3 text-white font-medium">{equipe}</td>
                        <td className="text-center px-4 py-3 text-slate-300">{stats.count}</td>
                        <td className="text-center px-4 py-3">
                          <span className="text-amber-400 font-bold">{stats.moyenne}j</span>
                        </td>
                        <td className="text-center px-4 py-3 text-green-400">{stats.min}j</td>
                        <td className="text-center px-4 py-3 text-red-400">{stats.max}j</td>
                        <td className="text-center px-4 py-3 text-slate-400">{stats.ecartType}j</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}

export default StatistiquesParcoursPage;
